import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent } from 'react';
import { FileText, Send, SmilePlus, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/user-messages';

interface ChatInputProps {
  onSend: (
    body: string,
    files?: File[],
    onProgress?: (percent: number) => void,
  ) => void | Promise<void>;
  onTyping?: (typing: boolean) => void;
  disabled?: boolean;
}

export function ChatInput({ onSend, onTyping, disabled }: ChatInputProps) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number>();
  const typingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '24px'; // Reset to calculate properly
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  }, [text]);

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) return;

    setError('');
    if (files.length > 0) setUploadProgress(0);
    try {
      await onSend(trimmed, files, setUploadProgress);
      setText('');
      setFiles([]);
      setUploadProgress(undefined);
    } catch (sendError) {
      setError(getErrorMessage(sendError, 'general'));
      setUploadProgress(undefined);
      return;
    }

    if (typingRef.current) {
      onTyping?.(false);
      typingRef.current = false;
    }

    // Reset height manually
    if (textareaRef.current) {
      textareaRef.current.style.height = '24px';
      textareaRef.current.focus();
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleChange(val: string) {
    setText(val);
    if (val.length > 0 && !typingRef.current) {
      onTyping?.(true);
      typingRef.current = true;
    } else if (val.length === 0 && typingRef.current) {
      onTyping?.(false);
      typingRef.current = false;
    }
  }

  function addFiles(selected: FileList | null) {
    if (!selected) return;
    const next = [...files, ...Array.from(selected)].slice(0, 5);
    const tooLarge = next.find((file) => file.size > 50 * 1024 * 1024);
    if (tooLarge) {
      setError(`${tooLarge.name} is larger than 50 MB.`);
      return;
    }
    setFiles(next);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function insertEmoji(emoji: string) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? text.length;
    const end = textarea?.selectionEnd ?? start;
    const nextText = `${text.slice(0, start)}${emoji}${text.slice(end)}`;
    handleChange(nextText);
    setShowEmoji(false);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  }

  const emojis = ['😀', '😂', '😊', '👍', '🙏', '❤️', '🎉', '✅', '👋', '😮', '😢', '💡'];

  return (
    <form
      onSubmit={handleSubmit}
      className="p-3 bg-primary/5 border-t border-primary/10 backdrop-blur-md"
    >
      {showEmoji && (
        <div className="mb-2 grid grid-cols-6 gap-1 rounded-2xl border border-border bg-background p-2 shadow-sm">
          {emojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              aria-label={`Insert ${emoji}`}
              title={`Insert ${emoji}`}
              className="flex h-8 items-center justify-center rounded-lg text-lg transition-colors hover:bg-muted"
              onClick={() => insertEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${file.size}-${index}`}
              className="flex max-w-full items-center gap-1.5 rounded-xl border border-border bg-background px-2.5 py-1.5 text-xs"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="max-w-40 truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() =>
                  setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))
                }
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </span>
          ))}
        </div>
      )}
      {error && <p className="mb-2 text-xs font-medium text-destructive">{error}</p>}
      {uploadProgress !== undefined && (
        <div className="mb-2">
          <div className="mb-1 flex justify-between text-[10px] font-medium text-muted-foreground">
            <span>Uploading attachments</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}
      <div className="flex items-end gap-2 glass-input border-primary/20 rounded-2xl p-2 transition-all focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 shadow-[inset_0_1px_4px_rgba(13,148,136,0.05)]">
        {/* Optional Attachments button placeholder */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground shrink-0 rounded-full"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach files"
        >
          <Paperclip className="h-5 w-5" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
          onChange={(event) => addFiles(event.target.files)}
        />

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message..."
          className="flex-1 max-h-[120px] min-h-[24px] bg-transparent border-0 focus:ring-0 resize-none py-1.5 px-2 text-[15px] outline-none placeholder:text-muted-foreground/60 custom-scrollbar"
          rows={1}
          disabled={disabled}
          style={{ height: '24px' }}
        />

        {/* Optional Emoji button placeholder */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground shrink-0 rounded-full"
          disabled={disabled}
          onClick={() => setShowEmoji((current) => !current)}
          aria-label="Choose emoji"
        >
          <SmilePlus className="h-5 w-5" />
        </Button>

        {/* Send Button changes color when text is entered */}
        <Button
          type="submit"
          size="icon"
          className={cn(
            'h-9 w-9 shrink-0 rounded-full transition-all duration-200',
            text.trim()
              ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-glass-md shadow-primary/40 clay-button'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 shadow-glass-sm',
          )}
          disabled={disabled || (!text.trim() && files.length === 0)}
        >
          <Send className="h-4 w-4 ml-0.5" />
        </Button>
      </div>
    </form>
  );
}

// Utility class needed locally since we removed it from import
function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}
